// PlaybookSearchHero — the home page's primary surface.
//
// Original UX restored:
//   • Rotating call-to-action chip above the headline (shared tagline).
//   • Big headline: "Learn AI. Hack Anything."
//   • Search bar with a "Run" button at the END of the input.
//   • Prompt bubbles BELOW the search bar — clicking one inserts its
//     prompt into the search input (does NOT auto-run; the user can edit
//     and then hit Run).
//
// Hitting Run picks the best-matching playbook for the typed query (or
// falls back to the first one) and hands it to the InlinePlaybookRunner
// chat with the user's text seeded into the composer.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, X, Zap, BookOpen, Target, Loader2, Compass, Lightbulb } from "lucide-react";
import { PLAYBOOKS, type Playbook } from "@/data/playbooks";
import UnifiedChatShell from "@/components/chat/UnifiedChatShell";
import { Map as MapIcon } from "lucide-react";
import { usePlaybookProgress } from "@/hooks/usePlaybookProgress";
import { useMasteryJourney } from "@/hooks/useMasteryJourney";
import { useMasteryHeroPrefs } from "@/hooks/useMasteryHeroPrefs";
import MasteryHeroSettings from "@/components/home/MasteryHeroSettings";
import RoadmapDrawer from "@/components/home/RoadmapDrawer";
import {
  AI_MASTERY_PATH,
  AI_BUILDER_SKILLS,
  MASTERY_TIERS,
  type MasterySkill,
} from "@/data/aiMasteryPath";
import {
  playbookForMasterySkill,
  findMasterySkillPlaybook,
} from "@/data/masterySkillPlaybooks";
import { trackHeroSearch } from "@/lib/funnelTracker";
import { subscribeHomeChatLaunch } from "@/lib/homeChatLaunchBus";

// Splits `text` on the union of `tokens` (case-insensitive, whole word
// or substring) and wraps every match in a primary-tinted <mark>. We
// escape regex metacharacters so user input like "c++" or "n8n.io"
// can't blow up the matcher. Returns a fragment-friendly array.
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function Highlight({
  text,
  tokens,
  className,
}: {
  text: string;
  tokens: string[];
  className?: string;
}) {
  if (!text) return null;
  const valid = tokens.filter((t) => t && t.length >= 2);
  if (valid.length === 0) return <>{text}</>;
  // Longest-first so "automation" wins over "auto" when both are typed.
  const sorted = [...valid].sort((a, b) => b.length - a.length);
  const re = new RegExp(`(${sorted.map(escapeRegex).join("|")})`, "gi");
  const testRe = new RegExp(`^(?:${sorted.map(escapeRegex).join("|")})$`, "i");
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) =>
        testRe.test(part) ? (
          <mark
            key={i}
            className={
              className ??
              "bg-primary/15 text-primary rounded-sm px-0.5 -mx-0.5"
            }
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

// Local model shortcut (mirrors src/data/playbooks.ts) so we can build
// an ad-hoc "Learn X" challenge from a free-form search without
// importing internal playbook constants.
const CUSTOM_PRO = { id: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" };

// Pure scoring routine for the live-results panel. Lives at module
// scope (not inside the component) so the per-query cache can call it
// without capturing any component state, and so the function identity
// stays stable across renders. `key` MUST already be trimmed +
// lowercased — the cache uses the same normalization for its keys.
function computeLiveResults(key: string) {
  const STOP = new Set([
    "a", "an", "and", "the", "to", "for", "with", "of", "on",
    "in", "into", "from", "my", "me", "i", "you", "your", "how",
    "what", "build", "learn", "create", "make", "do",
  ]);
  const tokens = key.split(/[^a-z0-9]+/).filter((t) => t && !STOP.has(t));
  if (tokens.length === 0) return null;

  const score = (haystack: string) => {
    const h = haystack.toLowerCase();
    let s = 0;
    for (const t of tokens) if (h.includes(t)) s += 1;
    // Whole-phrase bonus so "cold email" beats "cold compress" + "email tips".
    if (h.includes(key)) s += 2;
    return s;
  };

  const playbookHits = PLAYBOOKS
    .map((p) => {
      const haystack = [p.title, p.tagline, ...p.steps.map((s) => s.title)].join(" ");
      return { kind: "playbook" as const, item: p, score: score(haystack) };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  const skillPool = [...AI_MASTERY_PATH, ...AI_BUILDER_SKILLS];
  const skillHits = skillPool
    .map((s) => ({ kind: "skill" as const, item: s, score: score(s.label) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  // Highlight tokens — the typed phrase plus each non-stopword token.
  // We dedupe and drop sub-2-char tokens because <mark>ing single
  // letters creates visual noise without helping scan-ability.
  const highlightTokens = Array.from(
    new Set([key, ...tokens].filter((t) => t.length >= 2)),
  );
  return {
    playbookHits,
    skillHits,
    total: playbookHits.length + skillHits.length,
    highlightTokens,
  };
}

// The home-page rotator surfaces three skills from the 100-step AI
// Mastery Path at a time. Tapping a skill seeds the search input with
// the skill name AND immediately picks the best-matching playbook for
// it, so a click launches straight into the runner.

export default function PlaybookSearchHero() {
  const [query, setQuery] = useState("");
  // Debounced mirror of `query` used ONLY by the live results panel
  // below the search bar. The input itself stays controlled by `query`
  // so typing feels instant; results recompute on a short trailing
  // delay so the panel doesn't flicker on every keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [active, setActive] = useState<Playbook | null>(null);
  // Bumped on every external launch so UnifiedChatShell remounts with a
  // fresh conversation id. The hook `useAutoSaveChat` inside the shell
  // has already persisted the previous turns to `chat_conversations`,
  // so the prior chat lives on in the user's "Past chats" sidebar —
  // this just guarantees a clean slate, ChatGPT-style.
  const [launchKey, setLaunchKey] = useState(0);
  // Source label of the most recent external launch (e.g. "feed:quest")
  // — surfaced through the chat shell's seed pill so the user knows
  // which card spawned the conversation.
  const [launchSource, setLaunchSource] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { progress, clear } = usePlaybookProgress();
  const { recordSkillLaunch } = useMasteryJourney();
  const { prefs: heroPrefs } = useMasteryHeroPrefs();

  // Per-query result cache. The `liveResults` useMemo already skips
  // recomputation when `debouncedQuery` is referentially stable, but
  // common typing patterns (backspace, retype, paste-then-trim) feed
  // the same string in repeatedly across renders — and the cache lets
  // those revisits return in O(1) instead of re-walking PLAYBOOKS +
  // the 100-step mastery path. Keyed by the trimmed/lowercased query
  // so "  Cold Email " and "cold email" share an entry. Capped at 50
  // entries with a simple FIFO eviction so memory stays bounded as a
  // user explores. A ref (not state) — we never want a cache write to
  // trigger a re-render.
  const liveResultsCacheRef = useRef<Map<string, ReturnType<typeof computeLiveResults>>>(new Map());

  // 150ms is long enough to coalesce rapid typing but short enough
  // that the panel still feels live. Cleared on unmount + on every
  // new keystroke so only the trailing value lands.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, 150);
    return () => window.clearTimeout(id);
  }, [query]);

  // "Searching…" indicator state — true while the user has typed
  // something the debouncer hasn't caught up to yet (i.e. the live
  // results below are still showing the *previous* query's hits). We
  // gate on `>= 2` chars so single-letter typing doesn't flicker the
  // spinner before the results panel would even open. Trim both sides
  // so trailing whitespace from a paste doesn't keep the spinner stuck.
  const isSearching =
    query.trim().length >= 2 && query.trim() !== debouncedQuery.trim();

  // The runner accepts an optional `startAtStep` for one-shot handoff.
  // We stage it here so it gets passed on the very first render of the
  // runner, then cleared so subsequent re-renders / playbook chains
  // don't keep re-applying the same start index. The rotator no longer
  // sets this (one-tap-to-launch always starts at Step 1) — kept around
  // for any future surface that wants deep-step entry.
  const [pendingStartStep, setPendingStartStep] = useState<number | undefined>(undefined);

  // Inline roadmap picker — replaces the old Link to /roadmap so tapping
  // the "Roadmap to AI Mastery" pill stays on the home page and funnels
  // straight into the chat shell.
  const [roadmapOpen, setRoadmapOpen] = useState(false);

  // Live search results — when the user types, we surface the top
  // matching curated playbooks AND mastery skills directly below the
  // search bar so they can pick a known-good entry point with one tap
  // instead of pressing Run and getting whatever the fuzzy matcher
  // happened to score highest. Hidden when the query is empty (the
  // rotator + saved playbooks fill that space instead).
  const liveResults = useMemo(() => {
    const key = debouncedQuery.trim().toLowerCase();
    if (key.length < 2) return null;
    const cache = liveResultsCacheRef.current;
    const cached = cache.get(key);
    if (cached !== undefined) {
      // Refresh recency by re-inserting (Map preserves insertion order,
      // so this turns the FIFO into a tiny LRU for free).
      cache.delete(key);
      cache.set(key, cached);
      return cached;
    }
    const computed = computeLiveResults(key);
    cache.set(key, computed);
    if (cache.size > 50) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
    return computed;
  }, [debouncedQuery]);

  // Fire one analytics impression per debounced query: either `shown`
  // (≥1 curated match) or `no_results` (zero). The trackHeroSearch
  // helper already dedupes per (event, query) so React re-renders or
  // the user briefly retyping the same string won't inflate counts.
  // Click events are fired from the row handlers below.
  useEffect(() => {
    if (!liveResults) return;
    const q = debouncedQuery.trim();
    if (q.length < 2) return;
    if (liveResults.total === 0) {
      trackHeroSearch("no_results", q);
    } else {
      trackHeroSearch("shown", q, {
        playbook_hits: liveResults.playbookHits.length,
        skill_hits: liveResults.skillHits.length,
      });
    }
  }, [liveResults, debouncedQuery]);

  // Flat, display-ordered list of selectable rows for keyboard nav.
  // Playbooks render first, then mastery skills — keeping this order
  // in sync with the JSX below means ↓/↑ walks the panel exactly as
  // the eye does. We rebuild it on every `liveResults` change so the
  // active index always points at a real, currently-rendered row.
  type LiveRow =
    | { kind: "playbook"; item: Playbook }
    | { kind: "skill"; item: MasterySkill };
  const liveRows = useMemo<LiveRow[]>(() => {
    if (!liveResults || liveResults.total === 0) return [];
    return [
      ...liveResults.playbookHits.map(({ item }) => ({ kind: "playbook" as const, item })),
      ...liveResults.skillHits.map(({ item }) => ({ kind: "skill" as const, item })),
    ];
  }, [liveResults]);

  const [activeIndex, setActiveIndex] = useState(-1);
  // Reset the highlight whenever the result set changes — otherwise an
  // index from a previous query could point past the new array's end,
  // or worse, silently launch the wrong row when the user hits Enter.
  useEffect(() => {
    setActiveIndex(liveRows.length > 0 ? 0 : -1);
  }, [liveRows]);

  // Shared launcher so the keyboard path mirrors the click path 1:1
  // (same analytics payload, same handlers). Returns true when a row
  // actually launched so the caller can decide whether to swallow the
  // surrounding form's submit.
  const launchRow = (row: LiveRow): boolean => {
    if (row.kind === "playbook") {
      trackHeroSearch("click", debouncedQuery, {
        kind: "playbook",
        slug: row.item.slug,
        title: row.item.title,
      });
      handleOpenPlaybook(row.item);
      return true;
    }
    trackHeroSearch("click", debouncedQuery, {
      kind: "skill",
      skill_num: row.item.num,
      tier: row.item.tier,
      label: row.item.label,
    });
    handleSkillClick(row.item);
    return true;
  };

  // Keydown handler bound to the search input. Only ↓/↑/Home/End/Esc
  // are handled here — Enter falls through to the form's onSubmit so
  // the existing Run flow still works when the user hasn't picked a
  // specific row (activeIndex === -1, e.g. zero-results panel).
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (liveRows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % liveRows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? liveRows.length - 1 : i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(liveRows.length - 1);
    } else if (e.key === "Escape") {
      // Soft dismiss: drop the highlight but keep the panel + query so
      // the user can resume typing without losing context.
      setActiveIndex(-1);
    }
  };

  // Resolve the saved-progress slug back to a real playbook so we can
  // render a "Resume" card in the hero. If the playbook was renamed or
  // removed since the user last visited, we silently ignore the row.
  const resumePlaybook = useMemo(() => {
    if (!progress) return null;
    return PLAYBOOKS.find((p) => p.slug === progress.slug) ?? null;
  }, [progress]);

  // Returning-user default: when there's saved progress, seed the search
  // input with the user's last topic so Run continues their journey
  // instead of starting from a blank prompt. We only do this on first
  // mount (so the user can clear/edit freely after) and only when the
  // input is still empty — never clobber an in-progress query.
  const seededResumeRef = useRef(false);
  useEffect(() => {
    if (seededResumeRef.current) return;
    if (!resumePlaybook || !progress) return;
    if (query.trim().length > 0) return;
    setQuery(progress.topic || resumePlaybook.title);
    seededResumeRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePlaybook, progress]);

  // Pick the playbook that best matches the query, OR — when the user
  // typed something the curated set doesn't cover — synthesize an
  // ad-hoc "Learn X" challenge from their text so the chat shell opens
  // with THEIR idea instead of falling back to whatever PLAYBOOKS[0]
  // happens to be (which is what made every free-form search land on
  // the same default prompt).
  //
  // Scoring: count overlapping keywords across title / tagline / step
  // titles. Only return a curated playbook when there's a real hit;
  // otherwise return a one-step custom challenge that seeds the user's
  // text directly into the composer.
  const pickPlaybook = useMemo(() => {
    const STOPWORDS = new Set([
      "a", "an", "and", "the", "to", "for", "with", "of", "on",
      "in", "into", "from", "my", "me", "i", "you", "your", "how",
      "what", "build", "learn", "create", "make", "do",
    ]);
    const tokenize = (s: string) =>
      s
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((t) => t && !STOPWORDS.has(t));

    const buildCustomChallenge = (q: string): Playbook => {
      const topic = q.trim();
      const short = topic.length > 60 ? `${topic.slice(0, 57)}…` : topic;
      return {
        slug: `custom-${Date.now()}`,
        title: `Learn: ${short}`,
        tagline: "Your idea, coached step-by-step.",
        emoji: "✨",
        steps: [
          {
            title: "Frame the challenge",
            prompt:
              "I want to learn / build: {topic}\n\n" +
              "Act as my AI coach. First, restate the goal in one sentence. " +
              "Then break it into 3–5 concrete steps I can work through with you, " +
              "and ask me one clarifying question before we start.",
            model: CUSTOM_PRO.id,
            modelLabel: CUSTOM_PRO.label,
            reason:
              "Pro reasoning is best at turning a vague idea into a structured plan.",
          },
        ],
      };
    };

    return (q: string): Playbook => {
      const tokens = tokenize(q);
      if (tokens.length === 0) return PLAYBOOKS[0];

      let best: { p: Playbook; score: number } | null = null;
      for (const p of PLAYBOOKS) {
        const haystack = [
          p.title,
          p.tagline,
          ...p.steps.map((s) => `${s.title} ${s.modelLabel}`),
        ]
          .join(" ")
          .toLowerCase();
        let score = 0;
        for (const t of tokens) if (haystack.includes(t)) score += 1;
        if (!best || score > best.score) best = { p, score };
      }
      // A weak curated hit (score 1) shouldn't beat an exact mastery-
      // skill match — the generated mastery playbook teaches the skill
      // properly with multi-LLM steps, while a 1-token curated match is
      // often coincidental. Require score ≥ 2 OR no mastery hit.
      const masteryPool = [...AI_MASTERY_PATH, ...AI_BUILDER_SKILLS];
      const masteryHit = findMasterySkillPlaybook(q, masteryPool);
      if (best && best.score >= 2) return best.p;
      if (masteryHit) return masteryHit;
      if (best && best.score > 0) return best.p;
      return buildCustomChallenge(q);
    };
  }, []);

  const handleRun = () => {
    // If the user is a returning visitor and the search box still matches
    // their saved-resume topic, jump straight back into THAT playbook
    // (preserving their step progress) instead of fuzzy-matching a new
    // one. Once they edit the input, this short-circuit no longer fires.
    if (
      resumePlaybook &&
      progress &&
      query.trim().toLowerCase() === (progress.topic || resumePlaybook.title).trim().toLowerCase()
    ) {
      setActive(resumePlaybook);
      return;
    }
    setActive(pickPlaybook(query));
  };

  // One-tap launch: clicking a rotating mastery skill drops the visitor
  // straight into the inline chat runner with the best-matching playbook
  // for that skill. No preview, no picker — get them learning in one
  // tap. The skill label is seeded as the topic so the runner's first
  // turn is already aligned with what they clicked.
  const handleSkillClick = (skill: MasterySkill) => {
    setQuery(skill.label);
    setPendingStartStep(undefined);
    // Skill clicks ALWAYS open the rich generated mastery playbook so
    // every one of the 100+ skills teaches multi-LLM step-by-step (not
    // just the dozen that happen to keyword-match a curated PLAYBOOK).
    setActive(playbookForMasterySkill(skill));
    recordSkillLaunch(skill.label);
  };

  // Deep-link entry from /roadmap (the roadmap page). When the URL
  // carries `?skill=<label>`, auto-launch the matching playbook in
  // the unified chat shell so the visitor lands directly inside the
  // workspace they tapped on the roadmap. We strip the param after
  // consuming it so a back/refresh doesn't keep re-launching.
  useEffect(() => {
    const skillParam = searchParams.get("skill");
    if (!skillParam) return;
    setQuery(skillParam);
    setPendingStartStep(undefined);
    setActive(pickPlaybook(skillParam));
    recordSkillLaunch(skillParam);
    const next = new URLSearchParams(searchParams);
    next.delete("skill");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Bridge: any home-page surface (Learn Everything feed, Hackathon
  // CTA, Roadmap drawer) can fire `launchOnHome(...)` to drop the user
  // straight into a brand-new chat with the chosen playbook. Bumping
  // `launchKey` remounts the shell so the previous conversation is
  // archived (auto-save already wrote it) and a fresh one starts.
  useEffect(() => {
    return subscribeHomeChatLaunch((p) => {
      setQuery(p.topic);
      setPendingStartStep(p.startStep);
      setLaunchSource(p.source);
      setActive(p.playbook);
      setLaunchKey((k) => k + 1);
      recordSkillLaunch(p.topic);
      // Smooth-scroll the hero into view so the user sees the chat
      // appear instead of wondering why nothing happened.
      requestAnimationFrame(() => {
        const el = document.getElementById("home-hero-chat-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Direct launch from a live-results row: same flow as Run but skips
  // the fuzzy matcher because the user already picked a specific
  // playbook from the dropdown.
  const handleOpenPlaybook = (pb: Playbook) => {
    setPendingStartStep(undefined);
    setActive(pb);
  };

  // When a playbook is active, the runner takes over the section.
  if (active) {
    return (
      // New visitors land in the unified chat-first workspace: top
      // mode bar (Chat / Image / Research / Analyze / Builder /
      // Library), persistent transcript across modes, model picker
      // top-right. The selected playbook is surfaced as a step strip
      // above the transcript and its first step's prompt is preloaded
      // into the composer so the visitor can hit Send immediately.
      <section id="home-hero-chat-anchor" className="container mx-auto px-4 py-6 scroll-mt-24">
        <UnifiedChatShell
          // Remount on every external launch so the prior conversation
          // (already auto-saved to chat_conversations) is replaced by a
          // fresh chat — ChatGPT-style "new chat" behavior.
          key={`shell:${active.slug}:${launchKey}`}
          playbook={active}
          topic={query}
          initialMode="chat"
          initialModel={active.steps[0]?.model}
          initialComposerText={
            active.steps[0]
              ? active.steps[0].prompt.replace(
                  /\{topic\}/g,
                  query.trim() || "(your topic here)",
                )
              : query
          }
          onExit={() => {
            setActive(null);
            setPendingStartStep(undefined);
            setLaunchSource(null);
            requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
          }}
        />
      </section>
    );
  }

  return (
    <section className="relative py-10 sm:py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative z-10 mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto"
        >
          <RoadmapDrawer
            open={roadmapOpen}
            onOpenChange={setRoadmapOpen}
            onLaunchSkill={(s) => handleSkillClick(s)}
          />

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-mono font-extrabold tracking-tight mb-3 leading-[1.1]">
            <span className="block">
              <span className="text-foreground">Master </span>
              <span className="text-primary text-glow">AI</span>
              <span className="text-foreground">.</span>
            </span>
            <span className="block text-foreground">Hack Anything.</span>
          </h1>
          {/* Spacer where the rotating tagline used to live — kept
              modest so the search bar stays close to the H1. */}
          <div className="mb-7" />

          {/* Search bar with Run button at the end */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Enter on a highlighted live-result row launches THAT
              // row instead of running the fuzzy matcher — gives the
              // dropdown the same authority as a click.
              if (activeIndex >= 0 && liveRows[activeIndex]) {
                launchRow(liveRows[activeIndex]);
                return;
              }
              handleRun();
            }}
            className="relative max-w-2xl mx-auto mb-5"
          >
            <Sparkles className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-primary pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="What do you want to build or learn? (e.g. 'cold email', 'meeting notes')"
              className="w-full pl-14 pr-28 sm:pr-32 py-4 rounded-2xl bg-card border border-primary/30 text-foreground placeholder:text-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all border-glow"
              aria-label="What do you want to build or learn"
              role="combobox"
              aria-expanded={!!liveResults && liveResults.total > 0}
              aria-controls="hero-live-results"
              aria-autocomplete="list"
              aria-activedescendant={
                activeIndex >= 0 && liveRows[activeIndex]
                  ? `hero-live-row-${activeIndex}`
                  : undefined
              }
            />
            {/* Subtle "still computing" spinner — slides in just left
                of the Run button while the debouncer hasn't caught up
                to what the user is typing. Pointer-events-none so it
                never steals clicks. aria-live=polite so a screen
                reader hears "Searching" once per stale window without
                being spammed on every keystroke. */}
            {isSearching && (
              <span
                className="absolute right-[5.25rem] sm:right-24 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground pointer-events-none"
                aria-live="polite"
                aria-label="Searching"
              >
                <Loader2 className="w-3 h-3 animate-spin text-primary" />
                <span className="hidden sm:inline">Searching…</span>
              </span>
            )}
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs sm:text-sm font-mono font-semibold hover:bg-primary/90 active:scale-[.98] transition-all"
              aria-label="Run"
            >
              Run <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>

          {/* Roadmap to AI Mastery + settings — placed directly below the
              search bar so the search input is the first interactive
              element, with the roadmap entry point one tap away. */}
          <div className="mb-5 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setRoadmapOpen(true)}
              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-card/60 hover:border-primary/60 hover:bg-card transition-all text-xs sm:text-sm font-mono text-foreground"
              title="Open Roadmap to AI Mastery"
            >
              <MapIcon className="w-3.5 h-3.5 text-primary" />
              <span>Roadmap to <span className="text-primary">AI Mastery</span></span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
                100 skills · launch in chat
              </span>
            </button>
            <MasteryHeroSettings
              onLaunchSkill={(s) => handleSkillClick(s)}
            />
          </div>

          {/* Live search results — appear directly under the search bar
              the moment the user types ≥2 chars. Two compact sections
              ("Playbooks" + "Mastery skills") so visitors can see at a
              glance which curated entry points match their query. Each
              row launches into the chat shell on click, exactly like
              hitting Run, but with the specific item they picked
              instead of whatever the fuzzy matcher would have chosen.
              When there are zero matches we still show a quiet hint so
              the user knows Run will spin up a custom challenge from
              their text. */}
          {liveResults && (
            <div
              id="hero-live-results"
              role="listbox"
              aria-label="Search results"
              className={`max-w-2xl mx-auto mb-5 -mt-1 text-left rounded-xl border border-primary/20 bg-card/80 backdrop-blur-sm overflow-hidden transition-opacity duration-200 ${isSearching ? "opacity-60" : "opacity-100"}`}
            >
              {liveResults.total === 0 ? (
                <div className="px-3 py-3 space-y-2.5">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    <p className="text-[11px] font-mono text-muted-foreground leading-relaxed">
                      No curated match for{" "}
                      <span className="text-foreground">"{debouncedQuery.trim()}"</span> —
                      hit <span className="text-primary">Run</span> to spin up a
                      custom challenge, or try one of these:
                    </p>
                  </div>
                  {/* Example searches — tap to seed the input. We use
                      onMouseDown so the suggestion fires before the
                      input loses focus on click, keeping the user's
                      flow inside the search bar. */}
                  <div className="flex flex-wrap items-center gap-1.5 pl-5">
                    <Lightbulb className="w-3 h-3 text-primary/70 shrink-0" />
                    {["cold email", "meeting notes", "writing prompts", "ai agent", "image generation"].map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQuery(ex);
                          inputRef.current?.focus();
                        }}
                        className="px-2 py-0.5 rounded-full border border-border/60 bg-background/60 hover:border-primary/50 hover:bg-primary/5 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-all"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                  {/* Browse-top-tools shortcut — escape hatch for users
                      who'd rather scan the directory than refine their
                      query. Routes to the trending tab on /tools. */}
                  <Link
                    to="/tools?tab=trending"
                    className="ml-5 inline-flex items-center gap-1 text-[10px] font-mono text-primary hover:text-primary/80 hover:underline underline-offset-2"
                  >
                    <Compass className="w-3 h-3" />
                    Browse top tools
                    <ArrowRight className="w-2.5 h-2.5" />
                  </Link>
                </div>
              ) : (
                <>
                  {liveResults.playbookHits.length > 0 && (
                    <div className="border-b border-border/60 last:border-b-0">
                      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
                        <BookOpen className="w-3 h-3 text-primary" />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                          Playbooks
                        </span>
                      </div>
                      <ul className="pb-1">
                        {liveResults.playbookHits.map(({ item }, idx) => {
                          const rowIndex = idx; // playbooks come first in liveRows
                          const isActive = rowIndex === activeIndex;
                          return (
                          <li key={item.slug} role="option" aria-selected={isActive} id={`hero-live-row-${rowIndex}`}>
                            <button
                              type="button"
                              onMouseEnter={() => setActiveIndex(rowIndex)}
                              onClick={() => {
                                trackHeroSearch("click", debouncedQuery, {
                                  kind: "playbook",
                                  slug: item.slug,
                                  title: item.title,
                                });
                                handleOpenPlaybook(item);
                              }}
                              className={`group w-full flex items-center gap-2.5 px-3 py-2 hover:bg-primary/5 transition-colors text-left ${isActive ? "bg-primary/10" : ""}`}
                            >
                              <span aria-hidden className="text-base shrink-0">
                                {item.emoji}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-xs font-mono font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                  <Highlight
                                    text={item.title}
                                    tokens={liveResults.highlightTokens}
                                  />
                                </span>
                                <span className="block text-[10px] font-mono text-muted-foreground truncate">
                                  <Highlight
                                    text={item.tagline}
                                    tokens={liveResults.highlightTokens}
                                  />
                                </span>
                              </span>
                              <span className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider text-primary transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"}`}>
                                <Zap className="w-2.5 h-2.5" />
                                Launch
                              </span>
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {liveResults.skillHits.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
                        <Target className="w-3 h-3 text-primary" />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                          Mastery skills
                        </span>
                      </div>
                      <ul className="pb-1">
                        {liveResults.skillHits.map(({ item }, idx) => {
                          const meta = MASTERY_TIERS[item.tier];
                          const rowIndex = liveResults.playbookHits.length + idx;
                          const isActive = rowIndex === activeIndex;
                          return (
                            <li key={`${item.tier}-${item.num}-${item.label}`} role="option" aria-selected={isActive} id={`hero-live-row-${rowIndex}`}>
                              <button
                                type="button"
                                onMouseEnter={() => setActiveIndex(rowIndex)}
                                onClick={() => {
                                  trackHeroSearch("click", debouncedQuery, {
                                    kind: "skill",
                                    skill_num: item.num,
                                    tier: item.tier,
                                    label: item.label,
                                  });
                                  handleSkillClick(item);
                                }}
                                className={`group w-full flex items-center gap-2.5 px-3 py-2 hover:bg-primary/5 transition-colors text-left ${isActive ? "bg-primary/10" : ""}`}
                              >
                                <span
                                  className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider ${meta.pill}`}
                                >
                                  <span
                                    className={`w-1 h-1 rounded-full ${meta.dot}`}
                                    aria-hidden
                                  />
                                  {meta.headline}
                                </span>
                                <span className="flex-1 min-w-0 text-xs font-mono font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                  <Highlight
                                    text={item.label}
                                    tokens={liveResults.highlightTokens}
                                  />
                                </span>
                                <span className="shrink-0 text-[9px] font-mono text-muted-foreground">
                                  #{item.num}
                                </span>
                                <span className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider text-primary transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"}`}>
                                  <Zap className="w-2.5 h-2.5" />
                                  Launch
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Continue-your-journey hint — shows only while the search
              input still matches the user's last topic. Hitting Run
              picks up exactly where they left off (same playbook, same
              completed steps); editing the input switches back to the
              normal new-prompt flow. */}
          {resumePlaybook && progress && query.trim().toLowerCase() ===
            (progress.topic || resumePlaybook.title).trim().toLowerCase() && (
            <div className="max-w-2xl mx-auto -mt-2 mb-5 flex items-center justify-between gap-3 px-1">
              <p className="text-[11px] font-mono text-muted-foreground truncate text-left">
                <span className="text-primary">▶ Continue your journey:</span>{" "}
                <span className="text-foreground">{resumePlaybook.title}</span>{" "}
                <span className="text-muted-foreground">
                  ({progress.completedSteps.length}/{resumePlaybook.steps.length} steps)
                </span>
              </p>
              <button
                type="button"
                onClick={() => {
                  clear();
                  setQuery("");
                  inputRef.current?.focus({ preventScroll: true });
                }}
                aria-label="Start fresh"
                title="Start fresh — clear saved progress"
                className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

        </motion.div>
      </div>
    </section>
  );
}
